import { reverse } from 'named-urls'
import React, { useState } from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { ProductItemGroupedDetails } from '@components/common/ProductItemGroupedDetails/ProductItemGroupedDetails'
import { SideSummary } from '@components/common/SideSummary'
import {
  BackButton,
  Button,
  CurrencyInput,
  Division,
  HeadContent,
  Loading,
  ProductsList,
} from '@fourthwall/components'
import type { Price } from '@fourthwall/services/dist/models/common'
import type { OrderByShipments, OrderOffer } from '@fourthwall/services/dist/models/order'
import { getPrice } from '@fourthwall/utils'
import { routing } from '@utils/routing'

import * as S from './OrderRefundView.styled'

export type OrderRefundViewProps = {
  orderId: string
  friendlyId?: string
  order: OrderByShipments | undefined
  donation: Price | undefined
  isLoading: boolean
  remainingRefundAmount?: number
  onOrderRefund: (amount: string) => void
}

const OrderRefundView = ({
  orderId,
  friendlyId = '',
  order,
  donation,
  remainingRefundAmount = 0,
  isLoading,
  onOrderRefund,
}: OrderRefundViewProps) => {
  const [refundAmount, setRefundAmount] = useState('0')
  const parsedRefund = parseFloat(refundAmount)

  const handleRefundOrderClick = () => onOrderRefund(refundAmount)

  const renderOffer = ({ variant, offer, quantity, price, grouped }: OrderOffer) => (
    <Division size="small">
      <ProductsList.Item
        name={offer.name}
        description={variant.attributesDescription}
        details={grouped ? <ProductItemGroupedDetails groupedDetails={grouped} /> : undefined}
        image={offer.thumbnailImage}
        quantity={quantity}
        price={price}
        type={offer.offerType === 'GIFT_CARD' ? 'giftCard' : undefined}
      />
    </Division>
  )

  const renderDonation = () => {
    if (!donation) {
      return null
    }

    return (
      <Division size="small">
        <ProductsList.Item name="Donation" quantity={1} price={donation} type="donation" />
      </Division>
    )
  }

  const productsList = (
    <>
      <Division size="small" />
      <Division size="small">
        <ProductsList>
          {order?.offers.map(renderOffer)}
          {renderDonation()}
        </ProductsList>
      </Division>
      <Division size="small" />
    </>
  )

  const rightContent = (
    <SideSummary>
      <S.SummaryHeader>Refund amount</S.SummaryHeader>
      <CurrencyInput
        value={refundAmount}
        max={1_000_000}
        suggestion={<>{getPrice(remainingRefundAmount)} available for refund</>}
        onValueChange={setRefundAmount}
      />
      <S.SummaryButton>
        <Button
          label={`Refund ${getPrice(parseFloat(refundAmount) || 0)}`}
          appearance="primary"
          fullWidth
          multiline
          disabled={!parsedRefund || parsedRefund <= 0.99 || parsedRefund > remainingRefundAmount}
          onClick={handleRefundOrderClick}
        />
      </S.SummaryButton>
    </SideSummary>
  )

  return (
    <Loading isLoading={isLoading}>
      <HeadContent title={`Refund order (#${friendlyId})`} />
      <PageLayout leftContent={productsList} rightContent={rightContent}>
        <div>
          <Dashboard.ItemViewHeader
            name="Refund order"
            backButton={
              <BackButton
                label={`Order #${friendlyId}`}
                to={{ pathname: reverse(routing.contributions.orders.order.self, { orderId }) }}
              />
            }
          />
        </div>
      </PageLayout>
    </Loading>
  )
}
export default OrderRefundView
